// Card template for user role cards: profile data lives in a tab.
// Foreign profile: Profile tab first (default active). Own dashboard: Profile
// tab last. While the profile is unfilled the Profile tab is forced active
// and all other tabs are disabled; tabs are also disabled while editing so
// unsaved form input can't be lost by switching tabs.

import { useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import Card from './Card';
import Tabs from './Tabs';
import CardHeaderView from './CardHeaderView';
import LiveTab from './LiveTab';
import { useBackBtn, useLiveTournaments } from '../utils/hooks';
import './UserCardTemplate.scss';

export default function UserCardTemplate({
  header,
  className,
  backBtn,
  isTheUser,
  imgPath,
  imgThumbPath,
  imgTitle,
  name,
  details,
  editForm,
  placeholder,
  tabs = [],
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hash } = useLocation();
  const handleBackBtn = useBackBtn();
  const edit = searchParams.get('edit');
  const saved = searchParams.get('saved');

  // `saved` covers the window between submit and the Firestore listener
  // delivering the updated name
  const profileLocked = !name && !saved;

  // Force the Profile tab active and the edit form open in one navigation:
  // setting them separately loses one of the two, because setSearchParams
  // always drops the hash (react-router behavior, closed as intended)
  useEffect(() => {
    if (!profileLocked || (edit && hash === '#profile')) return;
    const params = new URLSearchParams(searchParams);
    params.set('edit', 'true');
    navigate(
      { hash: '#profile', search: '?' + params.toString() },
      { replace: true },
    );
  }, [profileLocked, edit, hash, searchParams, navigate]);

  const profileTab = {
    title: 'Profile',
    hash: '#profile',
    component: (
      <div className='profile-tab'>
        {edit && editForm ? (
          editForm
        ) : (
          <CardHeaderView
            imgPath={imgPath}
            imgThumbPath={imgThumbPath}
            imgTitle={imgTitle}
            name={name}
            details={details}
            allowEdit={!!editForm}
            placeholder={placeholder}
          />
        )}
      </div>
    ),
  };

  const otherTabs = tabs.map((tab) => ({
    ...tab,
    disabled: profileLocked || !!edit,
  }));

  // Live tab: only on the owner's dashboard and only while they have a
  // relevant tournament (published schedule, not ended, their team admitted
  // or referee games assigned). First tab, so it's the landing view.
  const { isReady: isLiveReady, entries: liveEntries } = useLiveTournaments();
  const liveTabs =
    isTheUser && isLiveReady && liveEntries.length
      ? [
          {
            title: 'Live',
            hash: '#live',
            component: <LiveTab entries={liveEntries} />,
            disabled: profileLocked || !!edit,
          },
        ]
      : [];

  const allTabs = isTheUser
    ? [...liveTabs, ...otherTabs, profileTab]
    : [profileTab, ...otherTabs];

  return (
    <Card
      header={header}
      className={'user-card-template ' + className}
      btnText={backBtn ? 'Back' : undefined}
      onClick={backBtn ? handleBackBtn : undefined}
      btnLeftArrow
    >
      <Tabs className={className + '-tabs'} tabs={allTabs} />
    </Card>
  );
}
